import { Box, Button, Flex, HStack, IconButton, Image, Text, VStack } from '@chakra-ui/react';
import type { FormEvent, KeyboardEvent, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { AdvisorAiDisclaimer } from './advisor-ai-disclaimer';
import { AgentAttachmentChips } from './agent-attachment-chips';
import { AGENT_CHAT_ACCEPT_ATTRIBUTE } from './agent-chat-attachments';
import {
  ADVISOR_PANEL_NEUTRAL_50_BG,
  ADVISOR_PANEL_NEUTRAL_100,
  ADVISOR_PANEL_NEUTRAL_250,
  ADVISOR_PANEL_PRIMARY_50,
  ADVISOR_PANEL_PRIMARY_500,
} from './advisor-panel-tokens';

/** Matches Figma AI Chat Modal Bottom spacing (Kin / advisor shells). */
export const AGENT_COMPOSE_SECTION_GAP_PX = '11px';

export const AGENT_COMPOSE_MAX_TEXTAREA_ROWS = 4;
export const AGENT_COMPOSE_LINE_HEIGHT_PX = 20;

const TOOLBAR_CONTROL_STYLES = {
  borderWidth: '1px',
  borderColor: ADVISOR_PANEL_NEUTRAL_100,
  bg: 'white',
  borderRadius: '4px',
} as const;

export type AgentComposeBarAttachments = {
  files: readonly File[];
  onAddFiles: (files: FileList | File[] | null | undefined) => void;
  onRemoveAt: (index: number) => void;
  isAtLimit: boolean;
  errorMessages: readonly string[];
  onClearErrors: () => void;
};

export type AgentComposeBarScreenShare = {
  iconSrc: string;
    shareControlTitle: string;
    activeSurfaceTitle: string | null;
    capturePending?: boolean;
  onToggleShare: () => void;
  onClearShare: () => void;
};

export type AgentComposeBarProps = {
  input: string;
  setInput: (v: string) => void;
  onSend: () => void | Promise<void>;
  busy: boolean;
  disabled: boolean;
  onStop: () => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
    accentHex: string;
  uploadIconSrc: string;
  sendIconSrc: string;
  composerAriaLabel: string;
    sendAriaLabel?: string;
  placeholder?: string;
  stopGeneratingLabel?: string;
  /** When set, shows bordered upload control + chips + errors (same layout as Kin org app). */
  attachments?: AgentComposeBarAttachments;
    screenShare?: AgentComposeBarScreenShare;
  /** Extra toolbar control after attach / share (e.g. the PREMIUM "Talk to a Human" button, frame `13372:48446`). */
  toolbarExtra?: ReactNode;
  /** False while a human agent owns the thread (PS-1581): the AI disclaimer would misdescribe their replies. */
  showAiDisclaimer?: boolean;
};

/**
 * Bottom compose region for agent drawers: optional file row, textarea row with trailing send/stop.
 * Shared width/padding with `AgentSidePanelFrame` (436px shell).
 */
export function AgentComposeBar({
  input,
  setInput,
  onSend,
  busy,
  disabled,
  onStop,
  textareaRef,
  accentHex,
  uploadIconSrc,
  sendIconSrc,
  composerAriaLabel,
  sendAriaLabel = 'Send message',
  placeholder = 'Ask Kin',
  stopGeneratingLabel = 'Stop generating',
  attachments,
  screenShare,
  toolbarExtra,
  showAiDisclaimer = true,
}: AgentComposeBarProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const files = attachments?.files ?? [];
  const screenShareIntent = Boolean(screenShare?.activeSurfaceTitle);
  const canSend = Boolean(input.trim()) || files.length > 0 || screenShareIntent;
  const uploadDisabled = disabled || busy || (attachments?.isAtLimit ?? false);
  const shareDisabled =
    disabled ||
    busy ||
    Boolean(screenShare?.capturePending) ||
    (attachments?.isAtLimit ?? false);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // Line box × max rows + vertical padding in the HStack row (~12px top + bottom).
    const maxH = AGENT_COMPOSE_LINE_HEIGHT_PX * AGENT_COMPOSE_MAX_TEXTAREA_ROWS + 20;
    el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
  }, [textareaRef]);

  useEffect(() => {
    autoResize();
  }, [input, autoResize]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (!canSend || disabled || busy) return;
    formRef.current?.requestSubmit();
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSend || disabled || busy) return;
    void onSend();
  };

  const trailingActionSlot = (
    <Flex align="center" justify="center" flexShrink={0} w="36px" alignSelf="stretch" pr="12px">
      {busy ? (
        <IconButton
          type="button"
          variant="ghost"
          colorPalette="gray"
          aria-label={stopGeneratingLabel}
          title={stopGeneratingLabel}
          onClick={onStop}
          color="gray.700"
          w="24px"
          h="24px"
          minW="24px"
          minH="24px"
          p={0}
          borderRadius="4px"
          _hover={{ color: 'red.600', bg: 'red.50' }}
        >
          <Box boxSize="2.5" bg="currentColor" borderRadius="2px" aria-hidden />
        </IconButton>
      ) : (
        <IconButton
          type="submit"
          variant="ghost"
          aria-label={sendAriaLabel}
          title={sendAriaLabel}
          disabled={disabled || !canSend}
          w="24px"
          h="24px"
          minW="24px"
          minH="24px"
          p={0}
          borderRadius="4px"
          _hover={
            !disabled && canSend
              ? {
                  bg: accentHex,
                  '& img': { filter: 'brightness(0) invert(1)' },
                }
              : {}
          }
        >
          <Image src={sendIconSrc} alt="" boxSize="24px" draggable={false} aria-hidden />
        </IconButton>
      )}
    </Flex>
  );

  return (
    <Box px={4} py={3} borderTopWidth="1px" borderTopColor={ADVISOR_PANEL_NEUTRAL_100} bg="white" flexShrink={0}>
      <form ref={formRef} onSubmit={handleFormSubmit} noValidate style={{ margin: 0, width: '100%' }}>
        <VStack align="stretch" gap={AGENT_COMPOSE_SECTION_GAP_PX}>
          {attachments || screenShare || toolbarExtra ? (
            <HStack gap={AGENT_COMPOSE_SECTION_GAP_PX} align="center">
              {attachments ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={AGENT_CHAT_ACCEPT_ATTRIBUTE}
                    hidden
                    aria-hidden
                    tabIndex={-1}
                    onChange={e => {
                      attachments.onAddFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <IconButton
                    type="button"
                    aria-label="Attach files"
                    title="Attach files"
                    w="32px"
                    h="32px"
                    minW="32px"
                    minH="32px"
                    p={0}
                    borderWidth={TOOLBAR_CONTROL_STYLES.borderWidth}
                    borderColor={TOOLBAR_CONTROL_STYLES.borderColor}
                    bg={TOOLBAR_CONTROL_STYLES.bg}
                    borderRadius={TOOLBAR_CONTROL_STYLES.borderRadius}
                    disabled={uploadDisabled}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Image src={uploadIconSrc} alt="" boxSize="20px" draggable={false} aria-hidden />
                  </IconButton>
                </>
              ) : null}
              {screenShare ? (
                <IconButton
                  type="button"
                  aria-label={screenShare.shareControlTitle}
                  title={screenShare.shareControlTitle}
                  w="32px"
                  h="32px"
                  minW="32px"
                  minH="32px"
                  p={0}
                  borderWidth={TOOLBAR_CONTROL_STYLES.borderWidth}
                  borderColor={screenShareIntent ? ADVISOR_PANEL_PRIMARY_500 : TOOLBAR_CONTROL_STYLES.borderColor}
                  bg={TOOLBAR_CONTROL_STYLES.bg}
                  borderRadius={TOOLBAR_CONTROL_STYLES.borderRadius}
                  disabled={shareDisabled}
                  onClick={screenShare.onToggleShare}
                >
                  <Image src={screenShare.iconSrc} alt="" boxSize="20px" draggable={false} aria-hidden />
                </IconButton>
              ) : null}
              {toolbarExtra}
            </HStack>
          ) : null}

          {screenShare && screenShare.activeSurfaceTitle ? (
            <HStack
              align="center"
              gap={2}
              py={1}
              px={2}
              borderRadius="4px"
              borderWidth="1px"
              borderColor={ADVISOR_PANEL_PRIMARY_500}
              bg={ADVISOR_PANEL_PRIMARY_50}
              alignSelf="flex-start"
              maxW="100%"
            >
              <Image src={screenShare.iconSrc} alt="" boxSize="16px" flexShrink={0} draggable={false} aria-hidden />
              <Text fontSize="xs" fontWeight="medium" color="gray.800" lineClamp={1}>
                Sharing: {screenShare.activeSurfaceTitle}
              </Text>
              <IconButton
                type="button"
                aria-label="Stop sharing screen"
                title="Stop sharing screen"
                variant="ghost"
                size="xs"
                minW="24px"
                h="24px"
                p={0}
                color="gray.700"
                disabled={disabled || busy || Boolean(screenShare.capturePending)}
                onClick={screenShare.onClearShare}
              >
                <Text as="span" fontSize="sm" aria-hidden>
                  ×
                </Text>
              </IconButton>
            </HStack>
          ) : null}

          {attachments ? (
            <AgentAttachmentChips
              attachments={files}
              onRemove={attachments.onRemoveAt}
              disabled={disabled}
            />
          ) : null}

          {attachments && attachments.errorMessages.length > 0 ? (
            <HStack gap={2} align="baseline" role="alert" fontSize="xs" color="red.600" flexWrap="wrap">
              <Text as="span">
                {attachments.errorMessages[0]}
                {attachments.errorMessages.length > 1 ? ` (+${attachments.errorMessages.length - 1} more)` : ''}
              </Text>
              <Button
                type="button"
                variant="plain"
                size="xs"
                textDecoration="underline"
                onClick={attachments.onClearErrors}
              >
                Dismiss
              </Button>
            </HStack>
          ) : null}

          <HStack
            gap={0}
            minH="48px"
            borderRadius="4px"
            bg={disabled ? 'gray.100' : ADVISOR_PANEL_NEUTRAL_50_BG}
            _focusWithin={{ boxShadow: `0 0 0 1px ${accentHex}` }}
            transition="box-shadow 0.15s"
            align="stretch"
            css={{
              '& textarea::placeholder': {
                color: ADVISOR_PANEL_NEUTRAL_250,
              },
            }}
          >
            <textarea
              ref={textareaRef}
              aria-label={composerAriaLabel}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              rows={1}
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                resize: 'none',
                padding: '12px 0 12px 12px',
                fontSize: '14px',
                lineHeight: `${AGENT_COMPOSE_LINE_HEIGHT_PX}px`,
                background: 'transparent',
                fontFamily: 'inherit',
                overflow: 'auto',
                color: '#000000',
              }}
            />
            {trailingActionSlot}
          </HStack>
        </VStack>
      </form>
      {showAiDisclaimer ? <AdvisorAiDisclaimer mt={2.5} /> : null}
    </Box>
  );
}
